import { useTranslation } from '@pancakeswap/localization'
import { Currency, CurrencyAmount, Percent, TradeType } from '@pancakeswap/sdk'
import { SmartRouter, SmartRouterTrade } from '@pancakeswap/smart-router/evm'
import { AutoColumn, Box, QuestionHelper, RowBetween, RowFixed, Text } from '@pancakeswap/uikit'
import { formatAmount } from '@pancakeswap/utils/formatFractions'
import { useMemo } from 'react'
import styled from 'styled-components'
import { Field } from 'state/swap/actions'
import FormattedPriceImpact from 'views/Swap/components/FormattedPriceImpact'
import { computeTradePriceBreakdown } from 'views/Swap/V3Swap/utils/exchange'
import { basisPointsToPercent } from 'utils/exchange'
import { useUserSlippage } from '@pancakeswap/utils/user'

interface SwapTradeDetailsProps {
  swapTrade?: SmartRouterTrade<TradeType> | null
  swapAmount?: CurrencyAmount<Currency>
  swapFromCurrency?: Currency
  swapToCurrency?: Currency
  isLoading?: boolean
}

const DetailsContainer = styled(Box)`
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: 16px;
  padding: 16px;
  margin-top: 16px;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
`

const SwapIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.secondary};
  color: white;
  font-size: 10px;
  margin-right: 8px;
  font-weight: bold;
`

export function SwapTradeDetails({
  swapTrade,
  swapAmount,
  swapFromCurrency,
  swapToCurrency,
  isLoading = false,
}: SwapTradeDetailsProps) {
  const { t } = useTranslation()
  const [allowedSlippage] = useUserSlippage()

  // Get execution price using SmartRouter
  const executionPrice = useMemo(() => swapTrade && SmartRouter.getExecutionPrice(swapTrade), [swapTrade])

  // Compute trade breakdown
  const { priceImpactWithoutFee, lpFeeAmount } = useMemo(
    () => computeTradePriceBreakdown(swapTrade),
    [swapTrade],
  )

  // Calculate slippage adjusted amounts using the same logic as swap page
  const slippageAdjustedAmounts = useMemo(() => {
    if (!swapTrade) return {}

    const pct = basisPointsToPercent(allowedSlippage)

    return {
      [Field.INPUT]: SmartRouter.maximumAmountIn(swapTrade, pct),
      [Field.OUTPUT]: SmartRouter.minimumAmountOut(swapTrade, pct),
    }
  }, [swapTrade, allowedSlippage])

  if (!swapTrade || !swapAmount || !swapFromCurrency || !swapToCurrency) {
    return null
  }

  const isExactIn = swapTrade.tradeType === TradeType.EXACT_INPUT
  const expectedOutput = swapTrade.outputAmount

  return (
    <DetailsContainer>
      <Text fontSize="14px" bold mb="12px" color="secondary">
        <SwapIcon>↻</SwapIcon>
        {t('Swap Details')}
      </Text>

      <AutoColumn gap="8px">
        {/* Input Amount */}
        <RowBetween>
          <RowFixed>
            <Text fontSize="14px" color="textSubtle">
              {t('Swap Input')}
            </Text>
          </RowFixed>
          <Text fontSize="14px" bold>
            {formatAmount(swapAmount, 4)} {swapFromCurrency.symbol}
          </Text>
        </RowBetween>

        {/* Expected Output */}
        <RowBetween>
          <RowFixed>
            <Text fontSize="14px" color="textSubtle">
              {t('Expected Output')}
            </Text>
          </RowFixed>
          <Text fontSize="14px" bold color="success">
            {isLoading ? '...' : `${formatAmount(expectedOutput, 4)} ${swapToCurrency.symbol}`}
          </Text>
        </RowBetween>

        {/* Minimum Received */}
        <RowBetween>
          <RowFixed>
            <Text fontSize="14px" color="textSubtle">
              {t('Minimum Received')}
            </Text>
            <QuestionHelper
              text={
                <Text color="black">
                  {t(
                    'Your transaction will revert if there is a large, unfavorable price movement before it is confirmed.',
                  )}
                </Text>
              }
              ml="4px"
              placement="top"
            />
          </RowFixed>
          <Text fontSize="14px">
            {isLoading
              ? '...'
              : slippageAdjustedAmounts[Field.OUTPUT]
              ? `${formatAmount(slippageAdjustedAmounts[Field.OUTPUT], 4)} ${swapToCurrency.symbol}`
              : '-'}
          </Text>
        </RowBetween>

        {/* Price Impact */}
        <RowBetween>
          <RowFixed>
            <Text fontSize="14px" color="textSubtle">
              {t('Price Impact')}
            </Text>
            <QuestionHelper
              text={
                <Text color="black">
                  {t('The difference between the market price and estimated price due to trade size.')}
                </Text>
              }
              ml="4px"
              placement="top"
            />
          </RowFixed>
          {isLoading ? (
            <Text fontSize="14px">...</Text>
          ) : priceImpactWithoutFee ? (
            <FormattedPriceImpact priceImpact={priceImpactWithoutFee} />
          ) : (
            <Text fontSize="14px">-</Text>
          )}
        </RowBetween>

        {/* Trading Fee */}
        <RowBetween>
          <RowFixed>
            <Text fontSize="14px" color="textSubtle">
              {t('Trading Fee')}
            </Text>
            <QuestionHelper
              text={
                <Text color="black">
                  {t(
                    'Fee ranging from 0.1% to 0.01% depending on the pool fee tier. You can check the fee tier by clicking the magnifier icon under the "Route" section.',
                  )}
                </Text>
              }
              ml="4px"
              placement="top"
            />
          </RowFixed>
          <Text fontSize="14px">
            {isLoading
              ? '...'
              : lpFeeAmount
              ? `${formatAmount(lpFeeAmount, 4)} ${swapFromCurrency.symbol}`
              : '-'}
          </Text>
        </RowBetween>

        {/* Execution Price */}
        {executionPrice && (
          <RowBetween>
            <RowFixed>
              <Text fontSize="14px" color="textSubtle">
                {t('Exchange Rate')}
              </Text>
            </RowFixed>
            <Text fontSize="14px">
              {isLoading
                ? '...'
                : `1 ${swapFromCurrency.symbol} = ${executionPrice.toSignificant(6)} ${
                    swapToCurrency.symbol
                  }`}
            </Text>
          </RowBetween>
        )}
      </AutoColumn>

      <Box mt="12px" p="8px" style={{ backgroundColor: 'rgba(108, 199, 186, 0.1)', borderRadius: '8px' }}>
        <Text fontSize="11px" color="textSubtle" style={{ fontStyle: 'italic' }}>
          ℹ️ {t('This swap will be executed first, then the liquidity will be added with the received tokens.')}
        </Text>
      </Box>
    </DetailsContainer>
  )
}
